/**
 * The Driver App's device storage (EP10-S15, `10/04` §3): one IndexedDB
 * database, the raw API behind a handful of promises.
 *
 * - `run`     — the cached day: the pulled snapshot, under the key `today`.
 * - `outbox`  — queued commands, keyed by idempotency key.
 * - `media`   — photos and signatures waiting to upload (EP10-S18).
 * - `cursor`  — the pull cursor and the session facts, under the key `meta`.
 * - `drafts`  — step checkpoints of a capture in progress, so a crash
 *               mid-delivery resumes where it stopped (EP10-S17, S18).
 *
 * Encryption at rest is the platform's (`10/04` §3); what is ours is the purge
 * on sign-out and after seven days without a successful sync.
 */

const NAME = "khazzin-driver";
const VERSION = 1;
export const STORES = ["run", "outbox", "media", "cursor", "drafts"] as const;
export type StoreName = (typeof STORES)[number];

let opening: Promise<IDBDatabase> | null = null;

function done<T>(request: IDBRequest<T>): Promise<T> {
	return new Promise((resolve, reject) => {
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error);
	});
}

function database(): Promise<IDBDatabase> {
	if (opening === null) {
		const request = indexedDB.open(NAME, VERSION);
		request.onupgradeneeded = () => {
			for (const store of STORES) {
				if (!request.result.objectStoreNames.contains(store)) {
					request.result.createObjectStore(store);
				}
			}
		};
		opening = done(request);
		// A failed open is retried by the next call rather than cached.
		opening.catch(() => {
			opening = null;
		});
	}
	return opening;
}

async function run<T>(
	store: StoreName,
	mode: IDBTransactionMode,
	act: (s: IDBObjectStore) => IDBRequest<T>,
) {
	const db = await database();
	return done(act(db.transaction(store, mode).objectStore(store)));
}

export function get<T>(store: StoreName, key: string): Promise<T | undefined> {
	return run(store, "readonly", (s) => s.get(key) as IDBRequest<T | undefined>);
}

export async function put(store: StoreName, key: string, value: unknown): Promise<void> {
	await run(store, "readwrite", (s) => s.put(value, key));
}

export async function del(store: StoreName, key: string): Promise<void> {
	await run(store, "readwrite", (s) => s.delete(key));
}

export function all<T>(store: StoreName): Promise<T[]> {
	return run(store, "readonly", (s) => s.getAll() as IDBRequest<T[]>);
}

/** Deletes the whole database. Blocked connections are closed first. */
export async function purgeDatabase(): Promise<void> {
	const pending = opening;
	opening = null;
	if (pending !== null) {
		try {
			(await pending).close();
		} catch {
			// Never opened; nothing to close.
		}
	}
	await done(indexedDB.deleteDatabase(NAME));
}
